import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Pagination,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Document, Page } from 'react-pdf';
// Side-effect import: registers the pdf.js worker URL once for the app.
// Already used by `FactsheetModal`/`BrochureModal`; importing here keeps
// any consumer of `DownloadFileRow` working without extra setup.
import '../../super-picker-public/components/detail/_pdf/pdfWorker';
import FilePreviewDialog from './FilePreviewDialog';
import { fetchPdfThumbnailBlob, fetchPreviewBlob } from '../services/api';
import { triggerForceDownload as deliverForceDownload } from '../../shared/utils/triggerForceDownload';
import ShareAssetButton from '../../shared/components/ShareAssetButton';

// Shared file-row visuals used by both `ProjectFilesPanel` (project resources
// tab, with a checkbox) and `DownloadFileRow` (highlights/ebook page and any
// future surface that needs a simple downloadable row without selection).

// ── File-kind palette ───────────────────────────────────────────────────
type FileKind = {
  color: string;
  Icon: typeof PictureAsPdfIcon;
  label: string;
};

const FILE_KINDS: Record<string, FileKind> = {
  pdf:  { color: '#D32F2F', Icon: PictureAsPdfIcon, label: 'PDF' },
  doc:  { color: '#1565C0', Icon: ArticleIcon,      label: 'DOC' },
  docx: { color: '#1565C0', Icon: ArticleIcon,      label: 'DOC' },
  txt:  { color: '#546E7A', Icon: ArticleIcon,      label: 'TXT' },
  rtf:  { color: '#546E7A', Icon: ArticleIcon,      label: 'RTF' },
  xls:  { color: '#2E7D32', Icon: TableChartIcon,   label: 'XLS' },
  xlsx: { color: '#2E7D32', Icon: TableChartIcon,   label: 'XLS' },
  csv:  { color: '#2E7D32', Icon: TableChartIcon,   label: 'CSV' },
  ppt:  { color: '#E65100', Icon: SlideshowIcon,    label: 'PPT' },
  pptx: { color: '#E65100', Icon: SlideshowIcon,    label: 'PPT' },
  key:  { color: '#E65100', Icon: SlideshowIcon,    label: 'KEY' },
  png:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  jpg:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  jpeg: { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  gif:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'GIF' },
  webp: { color: '#7B1FA2', Icon: ImageIcon,        label: 'IMG' },
  svg:  { color: '#7B1FA2', Icon: ImageIcon,        label: 'SVG' },
  mp4:  { color: '#3949AB', Icon: MovieIcon,        label: 'MP4' },
  mov:  { color: '#3949AB', Icon: MovieIcon,        label: 'MOV' },
  avi:  { color: '#3949AB', Icon: MovieIcon,        label: 'AVI' },
  mkv:  { color: '#3949AB', Icon: MovieIcon,        label: 'MKV' },
  webm: { color: '#3949AB', Icon: MovieIcon,        label: 'WEBM' },
  zip:  { color: '#5D4037', Icon: FolderZipIcon,    label: 'ZIP' },
  rar:  { color: '#5D4037', Icon: FolderZipIcon,    label: 'RAR' },
  '7z': { color: '#5D4037', Icon: FolderZipIcon,    label: '7Z'  },
};

const FALLBACK_FILE_KIND: FileKind = {
  color: '#546E7A',
  Icon: InsertDriveFileIcon,
  label: 'FILE',
};

function getExt(filename: string): string {
  const m = filename.match(/\.([^.\\/]+)$/);
  return m ? m[1].toLowerCase() : '';
}

export function getFileKind(filename: string): FileKind {
  const ext = getExt(filename);
  return FILE_KINDS[ext] || FALLBACK_FILE_KIND;
}

/** Extensions `FilePreviewDialog` can render inline (PDF + images). */
const PREVIEWABLE_IMAGE_EXTS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'svg',
]);

/** Direct media files the browser can play inline (no YouTube/Vimeo embed). */
const PREVIEWABLE_VIDEO_EXTS = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogg', 'ogv']);

export function isPreviewableVideo(filename: string): boolean {
  return PREVIEWABLE_VIDEO_EXTS.has(getExt(filename));
}

export function isPreviewableFile(filename: string): boolean {
  const ext = getExt(filename);
  return (
    ext === 'pdf' ||
    PREVIEWABLE_IMAGE_EXTS.has(ext) ||
    PREVIEWABLE_VIDEO_EXTS.has(ext)
  );
}

export function isPreviewableImageFile(filename: string): boolean {
  return PREVIEWABLE_IMAGE_EXTS.has(getExt(filename));
}

export function isPdfFile(filename: string): boolean {
  return getExt(filename) === 'pdf';
}

/**
 * Insert zero-width spaces after `_` and `-` so long filenames that use
 * those delimiters instead of whitespace can wrap at segment boundaries
 * without changing how the label looks or breaking inside words.
 */
export function formatLabelForWrap(text: string): string {
  return text.replace(/([_-])/g, '$1\u200B');
}

/** Phones, tablets, and iPads (touch-primary). Used for preview gating only. */
export function useTouchPreviewDevice(): boolean {
  return useMediaQuery('(hover: none) and (pointer: coarse)');
}

/**
 * Build a browser save-as filename from a human title (e.g. ebook name),
 * keeping a sensible extension from `fallbackFilename` or defaulting to `.pdf`.
 */
export function buildSafeDownloadFilename(
  preferredName: string,
  fallbackFilename?: string | null,
): string {
  const raw = (preferredName || fallbackFilename || 'download').trim();
  const base = raw.split(/[\\/]/).pop()?.trim() || 'download';
  const cleaned =
    base.replace(/[<>:"|?*\u0000-\u001f]/g, '').replace(/\s+/g, ' ').trim() || 'download';

  if (/\.[a-z0-9]{1,8}$/i.test(cleaned)) {
    return cleaned;
  }

  const fallbackExt = (fallbackFilename || '').match(/\.([a-z0-9]{1,8})$/i);
  if (fallbackExt) {
    return `${cleaned}.${fallbackExt[1]}`;
  }
  return `${cleaned}.pdf`;
}

// ── Helpers ────────────────────────────────────────────────────────────
export function triggerDownload(url: string, filename: string) {
  const safeName = buildSafeDownloadFilename(filename, filename);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  a.remove();
}

/** @see shared/utils/triggerForceDownload — WebView-safe blob delivery. */
export async function triggerForceDownload(url: string, filename: string): Promise<void> {
  const safeName = buildSafeDownloadFilename(filename, filename);
  await deliverForceDownload(url, safeName);
}

// Huttons portal download endpoint. All project-scoped file downloads go
// through this URL — the portal handles auth, audit logging, and serves
// the binary with the correct Content-Disposition. We only need to pass
// the project id and the canonical file name.
//
// Example:
//   buildProjectDownloadUrl('P2406001', 'USRfloorplan_measurements.pdf')
//   → 'https://portal.huttonsgroup.com/Project/DownloadFile?module=Download&id=P2406001&fileName=USRfloorplan_measurements.pdf'
const PROJECT_DOWNLOAD_ENDPOINT =
  'https://portal.huttonsgroup.com/Project/DownloadFile';

export function buildProjectDownloadUrl(projectId: string, fileName: string): string {
  const params = new URLSearchParams({
    module: 'Download',
    id: projectId,
    fileName,
  });
  return `${PROJECT_DOWNLOAD_ENDPOINT}?${params.toString()}`;
}

// Convenience wrapper: build the Huttons portal URL for the given
// project + file name and kick off the browser download in one call.
// `displayName` is optional — when provided it's used as the suggested
// save-as name; otherwise the portal's Content-Disposition takes over.
export function triggerProjectDownload(
  projectId: string,
  fileName: string,
  displayName?: string,
) {
  const url = buildProjectDownloadUrl(projectId, fileName);
  const downloadName = buildSafeDownloadFilename(displayName || fileName, fileName);
  void triggerForceDownload(url, downloadName);
}

export function formatFileSize(bytes: number | null | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}

export function formatUploadedDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Thumbnail ──────────────────────────────────────────────────────────
export function FileThumbnail({ filename }: { filename: string }) {
  const { color, Icon } = getFileKind(filename);
  return (
    <Box
      aria-hidden
      sx={{
        width: 52,
        height: 52,
        flexShrink: 0,
        borderRadius: 1,
        border: `1px solid ${color}33`,
        bgcolor: `${color}14`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ color, fontSize: 30 }} />
    </Box>
  );
}

// ── Live preview thumbnail ─────────────────────────────────────────────
// Renders an actual content preview when possible:
//   • Image files → the image itself, cover-cropped.
//   • PDFs        → first page: rendered locally via `react-pdf` on desktop,
//     fetched as a server-rendered JPEG on touch devices.
//   • Videos      → first frame via muted `<video preload="metadata">`.
//   • Portal URLs → fetched via `fetchPreviewBlob` (backend proxy) so
//     CORS and `Content-Disposition: attachment` don't block preview.
//   • Anything else (or no/failed URL) → falls back to `FileThumbnail`.
//
// Fetch / PDF render is deferred until the row scrolls into view (via
// `IntersectionObserver`) so a long list doesn't hammer the network on mount.
//
// Sized to look good at touch density: 64×64 on mobile, 72×72 on sm+.
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);

/** Portal assets need the backend preview proxy (CORS + inline disposition). */
function isPortalHostUrl(rawUrl: string): boolean {
  try {
    return new URL(rawUrl).hostname === 'portal.huttonsgroup.com';
  } catch {
    return false;
  }
}

/**
 * Cap concurrent thumbnail work. iOS (and the native WebView in particular)
 * gets killed by the memory spikes of several multi-MB blobs — and, for PDFs,
 * of pdf.js parsing them — being alive at the same time while scrolling.
 */
function createThumbGate(maxConcurrent: number) {
  let active = 0;
  const waiters: Array<() => void> = [];
  return {
    async acquire(): Promise<() => void> {
      if (active >= maxConcurrent) {
        await new Promise<void>((resolve) => {
          waiters.push(resolve);
        });
      }
      active += 1;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        active = Math.max(0, active - 1);
        const next = waiters.shift();
        if (next) next();
      };
    },
  };
}

/** pdf.js rasterises one document at a time (desktop only — see below). */
const pdfListThumbGate = createThumbGate(1);
/** Image thumbs are cheap to decode but still cap the parallel downloads. */
const imageThumbFetchGate = createThumbGate(4);

function snapshotPdfCanvasToObjectUrl(
  canvas: HTMLCanvasElement,
): Promise<string | null> {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          resolve(URL.createObjectURL(blob));
        },
        'image/jpeg',
        0.7,
      );
      return;
    }
    try {
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    } catch {
      resolve(null);
    }
  });
}

export function FilePreviewThumbnail({
  filename,
  url,
  previewImage,
  size = 'small',
}: {
  filename: string;
  url?: string | null;
  /**
   * Explicit cover/thumbnail URL that wins over the auto-derived
   * preview. When provided the box renders this image cover-cropped
   * regardless of the file's extension, falling back to the icon
   * tile if the image fails to load. Used for sources where the
   * server already supplies a curated thumbnail (e.g. EDM rows
   * have a `Thumbnail` column we'd rather show than rasterising
   * the first page of a 30 MB PDF).
   */
  previewImage?: string | null;
  /**
   * `small` keeps the inline 64–72px thumb used in dense lists.
   * `large` matches the marketing-style file card (110–128px) — used by
   * the redesigned `DownloadFileRow`.
   */
  size?: 'small' | 'large';
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchPreview = useTouchPreviewDevice();
  const ext = getExt(filename);
  const isImage = IMAGE_EXTS.has(ext);
  const isPdf = ext === 'pdf';
  const isVideo = PREVIEWABLE_VIDEO_EXTS.has(ext);
  const hasUsableUrl = Boolean(url) && url !== '#';
  const hasPreviewImage = Boolean(previewImage);
  const isPortalUrl = hasUsableUrl && isPortalHostUrl(url!);
  // Images preview everywhere; videos stay desktop-only. PDFs are the awkward
  // one: rasterising a thumbnail in pdf.js means downloading the whole file
  // and parsing it, and a list of 30 MB brochures gets the iOS WebView killed
  // no matter how well the work is serialised. Desktop still renders locally;
  // touch devices ask the backend for a ~30 KB JPEG of page 1 instead.
  const needsProxyFetch =
    hasUsableUrl &&
    !hasPreviewImage &&
    isPortalUrl &&
    (isImage || (!touchPreview && (isPdf || isVideo)));
  const usesServerPdfThumbnail = touchPreview && isPdf && !hasPreviewImage && isPortalUrl;
  // Roughly 2× the CSS box so the JPEG still looks sharp on a retina screen.
  const pdfThumbnailRequestWidth = size === 'large' ? 256 : 160;

  // Track whether the preview asset has failed so we can fall back to
  // the icon thumbnail without flashing a broken-image symbol.
  const [previewFailed, setPreviewFailed] = useState(false);
  // The src whose `load` has completed. Stored as the URL rather than a
  // boolean so the spinner can be derived per-src: a boolean flag toggled
  // from an effect loses the race against a cache hit, whose `load` fires
  // before passive effects run and would then be overwritten back to
  // "loading" forever (repro: SPA-navigate away and back).
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [blobPreviewUrl, setBlobPreviewUrl] = useState<string | null>(null);
  /**
   * Lightweight JPEG of page 1 — either snapshotted from the local pdf.js
   * canvas (desktop) or fetched from the backend renderer (touch). Kept after
   * the pdf.js Document is torn down, and after the row scrolls out of view.
   */
  const [pdfSnapshotUrl, setPdfSnapshotUrl] = useState<string | null>(null);
  // Defer fetch / PDF rasterisation until the row scrolls near the viewport.
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const thumbGateReleaseRef = useRef<(() => void) | null>(null);

  const releaseThumbGate = () => {
    thumbGateReleaseRef.current?.();
    thumbGateReleaseRef.current = null;
  };

  useEffect(() => {
    setPreviewFailed(false);
    setBlobPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPdfSnapshotUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    setInView(false);
    releaseThumbGate();
  }, [url, previewImage, filename]);

  useEffect(() => {
    if (hasPreviewImage || !hasUsableUrl || (!isPdf && !isImage && !isVideo)) return;
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setInView(entry.isIntersecting);
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasPreviewImage, hasUsableUrl, isPdf, isImage, isVideo]);

  // Portal PDFs/images/videos are proxied through our API (same pipeline as
  // `FilePreviewDialog`) so react-pdf / <img> get an inline blob URL
  // instead of hitting CORS or `Content-Disposition: attachment`.
  useEffect(() => {
    if (!needsProxyFetch || !inView || previewFailed) return;
    // Already have a JPEG snapshot — no need to re-download the PDF.
    if (isPdf && pdfSnapshotUrl) return;
    let cancelled = false;

    const run = async () => {
      const release = await (isPdf ? pdfListThumbGate : imageThumbFetchGate).acquire();
      if (cancelled) {
        release();
        return;
      }
      thumbGateReleaseRef.current = release;
      try {
        const { objectUrl } = await fetchPreviewBlob(url!);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          releaseThumbGate();
          return;
        }
        setBlobPreviewUrl(objectUrl);
        // Image / video thumbs don't hold the gate; PDFs release after snapshot.
        if (!isPdf) releaseThumbGate();
      } catch {
        if (!cancelled) setPreviewFailed(true);
        releaseThumbGate();
      }
    };

    void run();
    return () => {
      cancelled = true;
      releaseThumbGate();
    };
  }, [needsProxyFetch, inView, url, previewFailed, isPdf, pdfSnapshotUrl]);

  // Touch devices: the backend renders page 1 and we just show the JPEG.
  useEffect(() => {
    if (!usesServerPdfThumbnail || !inView || previewFailed || pdfSnapshotUrl) return;
    let cancelled = false;

    const run = async () => {
      const release = await imageThumbFetchGate.acquire();
      try {
        if (cancelled) return;
        const objectUrl = await fetchPdfThumbnailBlob(url!, pdfThumbnailRequestWidth);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPdfSnapshotUrl(objectUrl);
      } catch {
        // No thumbnail (unrenderable, oversized, renderer busy) — icon tile.
        if (!cancelled) setPreviewFailed(true);
      } finally {
        release();
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    usesServerPdfThumbnail,
    inView,
    url,
    previewFailed,
    pdfSnapshotUrl,
    pdfThumbnailRequestWidth,
  ]);

  useEffect(
    () => () => {
      if (blobPreviewUrl) URL.revokeObjectURL(blobPreviewUrl);
    },
    [blobPreviewUrl],
  );

  useEffect(
    () => () => {
      if (pdfSnapshotUrl?.startsWith('blob:')) URL.revokeObjectURL(pdfSnapshotUrl);
    },
    [pdfSnapshotUrl],
  );

  // Leaving the viewport: drop the heavy PDF blob / live Document, keep the
  // JPEG snapshot so scrolling back is instant and cheap.
  useEffect(() => {
    if (inView) return;
    setBlobPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    releaseThumbGate();
  }, [inView]);

  // Curated `previewImage` takes precedence over the auto-derived
  // preview path — if it loads, we render that image; if it errors,
  // we fall through to the icon tile (we don't try to fall back to
  // the PDF first-page render because the source server already
  // chose this thumbnail as the canonical preview).
  const showPreviewImage = hasPreviewImage && !previewFailed;
  const canPreviewImage =
    !showPreviewImage && hasUsableUrl && isImage && !previewFailed;
  const canPreviewPdf =
    !touchPreview && !showPreviewImage && hasUsableUrl && isPdf && !previewFailed;
  const canPreviewVideo =
    !touchPreview && !showPreviewImage && hasUsableUrl && isVideo && !previewFailed;
  const canPreview =
    canPreviewImage || canPreviewPdf || canPreviewVideo || Boolean(pdfSnapshotUrl);
  // Non-portal URLs load directly; portal URLs wait for the proxied blob.
  const effectivePreviewUrl =
    blobPreviewUrl ?? (needsProxyFetch ? null : url);

  const pdfRenderWidth = size === 'large' ? 360 : 200;

  const handlePdfPageRendered = () => {
    const canvas = containerRef.current?.querySelector('canvas');
    if (!(canvas instanceof HTMLCanvasElement)) {
      releaseThumbGate();
      return;
    }
    void snapshotPdfCanvasToObjectUrl(canvas).then((snapUrl) => {
      if (!snapUrl) {
        releaseThumbGate();
        return;
      }
      setPdfSnapshotUrl((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return snapUrl;
      });
      // Tear down pdf.js + full-file blob; the JPEG is enough for the list.
      setBlobPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      releaseThumbGate();
    });
  };

  const shouldRenderImg = Boolean(
    (showPreviewImage && previewImage) ||
      (!showPreviewImage && canPreviewImage && effectivePreviewUrl) ||
      pdfSnapshotUrl,
  );
  const activeImgSrc = showPreviewImage
    ? previewImage
    : pdfSnapshotUrl
      ? pdfSnapshotUrl
      : canPreviewImage
        ? effectivePreviewUrl
        : null;

  const imageLoading = shouldRenderImg && loadedSrc !== activeImgSrc;

  // Belt-and-braces for a src that was already decoded before this commit:
  // `complete` is true immediately, so no `load` event is coming.
  useLayoutEffect(() => {
    if (!shouldRenderImg) return;
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoadedSrc(activeImgSrc ?? null);
    }
  }, [shouldRenderImg, activeImgSrc]);

  const { color, Icon } = getFileKind(filename);
  const fallbackIconSize = size === 'large' ? 22 : 18;
  const loadingIconSize = size === 'large' ? 26 : 22;

  return (
    <Box
      ref={containerRef}
      aria-hidden
      sx={{
        // Larger than the icon-only thumbnail so previews are actually
        // legible on phones; still compact enough for dense lists.
        width:
          size === 'large' ? { xs: 82, sm: 96 } : { xs: 48, sm: 54 },
        height:
          size === 'large' ? { xs: 110, sm: 128 } : { xs: 64, sm: 72 },
        flexShrink: 0,
        borderRadius: size === 'large' ? 1.5 : 1,
        overflow: 'hidden',
        position: 'relative',
        border: showPreviewImage || canPreview
          ? '1px solid var(--color-border-strong)'
          : `1px solid ${color}33`,
        bgcolor: showPreviewImage || canPreview ? 'var(--color-surface-elevated)' : `${color}14`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Constrain the rasterised PDF canvas (rendered at width=200 for
        // crispness) down to the thumbnail box. !important is needed
        // because react-pdf sets explicit width/height attributes on
        // both the <canvas> and the wrapping <div>.
        '& canvas': {
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto !important',
          height: 'auto !important',
          display: 'block',
        },
        '& .react-pdf__Page, & .react-pdf__Document': {
          background: 'transparent !important',
          maxWidth: '100%',
          maxHeight: '100%',
        },
      }}
    >
      {showPreviewImage ? (
        // Curated thumbnail (e.g. EDM.Thumbnail).
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {imageLoading && (
            <CircularProgress
              size={26}
              thickness={5}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'var(--color-accent)',
              }}
            />
          )}
          <Box
            component="img"
            ref={imgRef}
            src={previewImage!}
            alt=""
            onLoad={() => setLoadedSrc(previewImage ?? null)}
            onError={() => setPreviewFailed(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              opacity: imageLoading ? 0 : 1,
              transition: 'opacity 150ms ease',
            }}
          />
        </Box>
      ) : pdfSnapshotUrl ? (
        <Box
          component="img"
          src={pdfSnapshotUrl}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : canPreviewImage && effectivePreviewUrl ? (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {imageLoading && (
            <CircularProgress
              size={26}
              thickness={5}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'var(--color-accent)',
              }}
            />
          )}
          <Box
            component="img"
            ref={imgRef}
            src={effectivePreviewUrl}
            alt=""
            onLoad={() => setLoadedSrc(effectivePreviewUrl ?? null)}
            onError={() => setPreviewFailed(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              opacity: imageLoading ? 0 : 1,
              transition: 'opacity 150ms ease',
            }}
          />
        </Box>
      ) : canPreviewPdf && inView && effectivePreviewUrl ? (
        <Document
          file={effectivePreviewUrl}
          onLoadError={() => {
            setPreviewFailed(true);
            releaseThumbGate();
          }}
          onSourceError={() => {
            setPreviewFailed(true);
            releaseThumbGate();
          }}
          loading={<Icon sx={{ color, fontSize: loadingIconSize }} />}
          error={<Icon sx={{ color, fontSize: loadingIconSize }} />}
          noData={<Icon sx={{ color, fontSize: loadingIconSize }} />}
        >
          <Page
            pageNumber={1}
            width={pdfRenderWidth}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onRenderSuccess={handlePdfPageRendered}
            onRenderError={() => {
              setPreviewFailed(true);
              releaseThumbGate();
            }}
            loading={<Icon sx={{ color, fontSize: loadingIconSize }} />}
            error={<Icon sx={{ color, fontSize: loadingIconSize }} />}
            canvasBackground="#fff"
          />
        </Document>
      ) : canPreviewVideo && inView && effectivePreviewUrl ? (
        <>
          <Box
            component="video"
            src={effectivePreviewUrl}
            muted
            playsInline
            preload="metadata"
            onError={() => setPreviewFailed(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.28)',
              pointerEvents: 'none',
            }}
          >
            <PlayCircleOutlineIcon
              sx={{ color: '#fff', fontSize: size === 'large' ? 40 : 30 }}
            />
          </Box>
        </>
      ) : usesServerPdfThumbnail && inView && !previewFailed && !pdfSnapshotUrl ? (
        <Icon sx={{ color, fontSize: loadingIconSize }} />
      ) : canPreview && inView && needsProxyFetch && !effectivePreviewUrl && !previewFailed ? (
        // Proxied blob still loading — keep the icon placeholder stable.
        <Icon sx={{ color, fontSize: loadingIconSize }} />
      ) : canPreview && !inView ? (
        <Icon sx={{ color, fontSize: loadingIconSize }} />
      ) : (
        <Icon sx={{ color, fontSize: fallbackIconSize }} />
      )}
    </Box>
  );
}

// ── DownloadFileRow ────────────────────────────────────────────────────
// Simplified file row (no checkbox, no selection). Reuses the same visual
// language as `FileRow` inside `ProjectFilesPanel`: tinted file thumbnail on
// the left, name + uploaded date in the middle, accent Download button on
// the right. Designed to be dropped into any list without needing the
// `ProjectFileItem` data shape.
export interface DownloadFileRowProps {
  filename: string;
  /** Optional display name override (defaults to `filename`). */
  displayName?: string;
  /** Short description shown under the title (e.g. "Market Outlook & Analysis"). */
  subtitle?: string;
  /** Raw byte count; rendered as the right-hand half of the meta line. */
  sizeBytes?: number;
  /** Public download URL. When omitted the row is rendered in a disabled state. */
  url?: string | null;
  /** Public download URL without agent details. Optional. */
  urlPublic?: string | null;
  /**
   * Curated thumbnail URL — wins over the auto-derived
   * (PDF first-page / image-extension) preview. Falls back to the
   * file-kind icon if the image fails to load.
   */
  previewImage?: string | null;
  /** ISO date string for the upload time. Optional. */
  uploadedAt?: string | null;
  /** Pre-formatted upload label (takes precedence over `uploadedAt`). */
  uploadedLabel?: string;
  /** Optional click target for the whole row (e.g. open a preview). */
  onClick?: () => void;
  onPreview?: () => void;
  onDownloadClick?: () => void;
  /** Force disabling preview and always showing the trailing action instead. */
  disablePreview?: boolean;
  /** Trailing action: download file (default) or copy a link to the clipboard. */
  actionMode?: 'download' | 'copy';
  /** Text copied when `actionMode` is `'copy'`. Defaults to `url`. */
  copyText?: string | null;
  /** Replaces the auto-derived file-kind label on the meta line. */
  typeLabel?: string;
  /**
   * Fetch the file as a blob before downloading so cross-origin CDN
   * URLs save to disk instead of opening in a new tab.
   */
  forceBlobDownload?: boolean;
  /**
   * When set (admin-only surfaces), show a share control under the
   * download/copy action for EDM/eBook rows.
   */
  shareAsset?: {
    id: string;
    kind: 'edm' | 'ebook';
    title: string;
    projectId?: string | null;
  } | null;
}

export function DownloadFileRow({
  filename,
  displayName,
  subtitle,
  sizeBytes,
  url,
  urlPublic,
  previewImage,
  uploadedAt,
  uploadedLabel,
  onClick,
  onPreview,
  onDownloadClick,
  disablePreview,
  actionMode = 'download',
  copyText,
  typeLabel: typeLabelOverride,
  forceBlobDownload = false,
  shareAsset = null,
}: DownloadFileRowProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchPreview = useTouchPreviewDevice();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const disabled = !url;
  const label = displayName || filename;
  const downloadName = buildSafeDownloadFilename(label, filename);
  const textToCopy = (copyText ?? url ?? '').trim();
  const copyDisabled = !textToCopy;
  // Only show an uploaded date when the underlying timestamp exists.
  // Some callers provide a pre-formatted label; we explicitly suppress
  // it when `uploadedAt`/`createdAt` is missing so we don't fall back to
  // "today" downstream.
  const hasUploadedAt = typeof uploadedAt === 'string' && uploadedAt.trim().length > 0;
  const dateText = hasUploadedAt ? (uploadedLabel ?? formatUploadedDate(uploadedAt)) : null;
  const sizeText = formatFileSize(sizeBytes ?? null);
  const { label: fileKindLabel } = getFileKind(filename);
  const typeLabel = typeLabelOverride ?? fileKindLabel;
  // The compact meta line shown at the bottom of the card ("PDF · 4.2 MB").
  // The upload date is rendered as its own small caption directly below
  // the title (see the markup below), so it is intentionally omitted
  // here to avoid showing the same value twice.
  const metaPieces: string[] = [];
  if (typeLabel) metaPieces.push(typeLabel);
  if (sizeText) metaPieces.push(sizeText);
  const metaLine = metaPieces.join(' · ');

  const previewable = disablePreview ? false : isPreviewableFile(filename);
  const isImage = isPreviewableImageFile(filename);
  const isPdf = isPdfFile(filename);
  const rowOpensPreview =
    previewable && (!touchPreview || isImage || isPdf);
  const showDownloadButton =
    !previewable || (touchPreview && !isImage && !isPdf);

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!url || downloading) return;
    if (onDownloadClick) onDownloadClick();
    setDownloading(true);
    try {
      if (forceBlobDownload) {
        await triggerForceDownload(url, downloadName);
      } else {
        triggerDownload(url, downloadName);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!textToCopy) return;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — no-op.
    }
  };

  const handleRowClick = () => {
    if (onClick) {
      onClick();
    } else if (url && rowOpensPreview) {
      if (onPreview) onPreview();
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <Box
        onClick={handleRowClick}
        role={onClick || (url && rowOpensPreview) ? 'button' : undefined}
        tabIndex={onClick || (url && rowOpensPreview) ? 0 : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        sx={{
          display: 'flex',
          // Top-aligned so the thumbnail anchors to the same baseline as
          // the title even when the title wraps onto two lines.
          alignItems: 'stretch',
          gap: { xs: 1.25, sm: 2 },
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          border: '1px solid var(--color-border-strong)',
          bgcolor: 'var(--color-surface)',
          cursor: onClick || (url && rowOpensPreview) ? 'pointer' : 'default',
          transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
          '&:hover': {
            borderColor: 'var(--color-accent)',
            // 1px outer halo so the accent ring lands evenly on every
            // edge. The previous `0 1px 0 ... inset` only highlighted
            // the top edge, which read as a partial / clipped border.
            boxShadow: '0 0 0 1px var(--color-accent)',
          },
        }}
      >
        <FilePreviewThumbnail
          filename={filename}
          url={url}
          previewImage={previewImage}
          size={isMobile ? 'small' : 'large'}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              lineHeight: 1.25,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              overflowWrap: 'break-word',
              wordBreak: 'normal',
              whiteSpace: 'normal',
            }}
            title={label}
          >
            {formatLabelForWrap(label)}
          </Typography>
          {/* Smaller "Uploaded …" caption sits directly under the title
              so the date reads as a property of the file rather than
              getting lost on the bottom meta line. Hidden when there is
              no date to show. */}
          {dateText && !isMobile && (
            <Typography
              variant="caption"
              sx={{
                color: 'var(--color-text-muted)',
                fontWeight: 500,
                mt: 0.25,
                fontSize: { xs: '0.72rem', sm: '0.75rem' },
                lineHeight: 1.3,
              }}
            >
              Uploaded {dateText}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: 'var(--color-text-muted)',
                mt: 0.25,
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                whiteSpace: 'normal',
              }}
              title={subtitle}
            >
              {formatLabelForWrap(subtitle)}
            </Typography>
          )}
          {/* Spacer pushes the meta line to the bottom of the card so the
              "PDF · 4.2 MB" anchor visually aligns with the thumbnail's
              bottom edge regardless of how tall the title block ends up. */}
          <Box sx={{ flex: 1 }} />
          {isMobile ? (
            (typeLabel || dateText || sizeText) && (
              <Stack spacing={0.25} sx={{ mt: 1 }}>
                {(typeLabel || sizeText) && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--color-text-muted)',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      fontSize: '0.78rem',
                    }}
                  >
                    {[typeLabel, sizeText].filter(Boolean).join(' · ')}
                  </Typography>
                )}
                {dateText && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--color-text-muted)',
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      lineHeight: 1.3,
                    }}
                  >
                    Uploaded {dateText}
                  </Typography>
                )}
              </Stack>
            )
          ) : (
            metaLine && (
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--color-text-muted)',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  fontSize: { xs: '0.78rem', sm: '0.82rem' },
                  mt: 1,
                }}
              >
                {metaLine}
              </Typography>
            )
          )}
        </Box>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={0.25}
          sx={{ flexShrink: 0, alignSelf: 'stretch', py: 0.5 }}
        >
          {showDownloadButton && (
            actionMode === 'copy' ? (
              <Tooltip title={copied ? 'Copied!' : 'Copy link'} arrow placement="left">
                <span>
                  <IconButton
                    onClick={handleCopy}
                    disabled={copyDisabled}
                    aria-label="Copy link"
                    size="small"
                    sx={{
                      color: copied ? '#22c55e' : 'var(--color-accent)',
                      p: 0.5,
                      '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                      '&.Mui-disabled': { color: 'var(--color-border-strong)' },
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={downloading ? 'Preparing download…' : 'Download file'} arrow placement="left">
                <span>
                  <IconButton
                    onClick={handleDownload}
                    disabled={disabled || downloading}
                    aria-label={downloading ? 'Preparing download' : 'Download file'}
                    size="small"
                    sx={{
                      color: 'var(--color-accent)',
                      p: 0.5,
                      '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                      '&.Mui-disabled': { color: 'var(--color-border-strong)' },
                    }}
                  >
                    {downloading ? (
                      <CircularProgress size={18} thickness={5} sx={{ color: 'var(--color-accent)' }} />
                    ) : (
                      <DownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )
          )}
          {shareAsset ? (
            <ShareAssetButton
              visible
              asset={{
                id: shareAsset.id,
                kind: shareAsset.kind,
                title: shareAsset.title || label,
                filename,
                url: url || '',
                projectId: shareAsset.projectId,
              }}
            />
          ) : null}
        </Stack>
      </Box>

      {url && rowOpensPreview && (
        <FilePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={label}
          url={url}
          urlPublic={urlPublic}
          filename={downloadName}
          onDownloadClick={onDownloadClick}
          forceBlobDownload={forceBlobDownload}
        />
      )}
    </>
  );
}

// ── PaginatedFileList ──────────────────────────────────────────────────
// Renders a list of files as `DownloadFileRow`s, paginated client-side.
// Defaults to 8 items per page (per the design spec). Pagination controls
// are only rendered when there's more than one page so a short list
// stays clean.
export interface PaginatedFileListItem extends DownloadFileRowProps {
  /** Stable key per item — typically the database/document id. */
  id: string;
}

export function PaginatedFileList({
  items,
  pageSize: propPageSize = 8,
  emptyLabel = 'No files to display.',
}: {
  items: PaginatedFileListItem[];
  pageSize?: number;
  emptyLabel?: string;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pageSize = isMobile ? 5 : propPageSize;

  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  // Reset to the first page whenever the underlying list shrinks past
  // the current page (e.g. user types in the search bar and filters
  // results down).
  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [page, pageCount]);

  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', px: 1, py: 1 }}>
        {emptyLabel}
      </Typography>
    );
  }

  const start = page * pageSize;
  const visible = items.slice(start, start + pageSize);

  return (
    <Box>
      {/* One column on phones (rows stack vertically as before); two
          columns from `sm` upward so desktop users see twice as many
          rows per page without changing the row card itself. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: { xs: 1, sm: 1.25 },
        }}
      >
        {visible.map(({ id, ...rowProps }) => (
          <DownloadFileRow key={id} {...rowProps} />
        ))}
      </Box>
      {pageCount > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: { xs: 1.25, sm: 1.5 } }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_e, p) => setPage(p - 1)}
            shape="rounded"
            siblingCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'var(--color-text-strong)',
                borderColor: 'var(--color-border-strong)',
                fontWeight: 600,
                '&.Mui-selected': {
                  bgcolor: 'var(--color-accent)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                },
                '&:hover': { bgcolor: 'var(--color-accent-soft)' },
              },
            }}
          />
        </Stack>
      )}
    </Box>
  );
}
